import { useCallback, useMemo, type ReactElement } from 'react';
import type { School } from '@/types/school';
import { useGetSchools } from '@/repositories/school-repository';
import { usePublishCluster, type ClusterDocument } from '@/repositories/clusters-repository';
import type { ClusterListItem } from '@/types/clusters';
import {
  SPECIALIZATION_AREA_PUBLISHED_SCHOOLS_TITLE,
  SPECIALIZATION_AREA_PUBLISH_DESCRIPTION_DISTRICT,
} from '@/constants/clusters-pathway-constants';
import { SchoolsDrawer } from '@/containers/PlanOfStudy/schools-drawer';
import {
  buildPublishedSchoolsSaveErrorHeading,
  buildPublishedSchoolsSaveSuccessHeading,
  getSchoolsListAndSelectionForMappingMode,
} from '@/containers/PlanOfStudy/plan-schools-mapping';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import { isPosReadOnly } from '@/utils/cp-context';

function asSchoolRefs(value: unknown): Array<{ nid: string }> {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: Array<{ nid: string }> = [];
  for (const item of value) {
    if (item && typeof item === 'object' && 'nid' in item) {
      const nid = String((item as { nid?: string }).nid ?? '').trim();
      if (nid) {
        out.push({ nid });
      }
    }
  }
  return out;
}

/** Build `checkedSchools` entries for cluster publish (legacy CPUI sends school metadata). */
function checkedSchoolsFromNids(
  nids: string[],
  allSchools: School[]
): Array<Record<string, unknown>> {
  return nids.map((nid) => {
    const s = allSchools.find((x) => x.nid === nid);
    return {
      nid,
      name: s?.name ?? nid,
      lowestGrade: 0,
      highestGrade: 12,
      schoolLevel: 'high',
    };
  });
}

export interface ClusterPublishDrawerProps {
  clusterId: string;
  clusterName: string;
  cluster: ClusterListItem;
  isOpen: boolean;
  onClose: () => void;
  drawerId?: string;
  disablePublish?: boolean;
}

/**
 * Same published-schools drawer UX as POS `PublishAndUpdateDrawer` (without schedule radios):
 * `SchoolsDrawer` in `published` mode, then `PUT /clusters/publish` with legacy payload shape.
 */
export function ClusterPublishDrawer({
  clusterId,
  clusterName,
  cluster,
  isOpen,
  onClose,
  drawerId = 'cluster-publish-drawer',
  disablePublish = false,
}: ClusterPublishDrawerProps): ReactElement | null {
  const { data: schools = [], isLoading: schoolsLoading } = useGetSchools();
  const publishMutation = usePublishCluster();
  const { showToast } = useToastNotifications();

  const sortedSchools = useMemo(
    () => [...schools].sort((a, b) => (a.name > b.name ? 1 : -1)),
    [schools]
  );

  const publishedSchools = useMemo(
    () => asSchoolRefs(cluster.publishedSchools),
    [cluster.publishedSchools]
  );
  const activeSchools = useMemo<Array<{ nid: string }>>(() => [], []);

  const { allSchools, initialSelectedNids, lockedNids } = useMemo(
    () =>
      getSchoolsListAndSelectionForMappingMode(
        'published',
        publishedSchools,
        activeSchools,
        sortedSchools
      ),
    [publishedSchools, activeSchools, sortedSchools]
  );

  const loading = schoolsLoading || publishMutation.isPending;
  const description = SPECIALIZATION_AREA_PUBLISH_DESCRIPTION_DISTRICT;
  const drawerKey = cluster
    ? `${clusterId}:publish:${publishedSchools.map((s) => s.nid).join(',')}:${activeSchools.map((s) => s.nid).join(',')}`
    : `${clusterId}:publish:loading`;

  const handlePrimary = useCallback(
    (checkedSchoolNids: string[]) => {
      if (isPosReadOnly()) {
        return;
      }
      if (publishMutation.isPending) {
        return;
      }
      void (async () => {
        try {
          const body: ClusterDocument = {
            id: cluster.id,
            name: cluster.name,
            description: cluster.description ?? '',
            lastPublished: cluster.lastPublished ?? null,
            publishedSchools: cluster.publishedSchools,
            pathways: cluster.pathways,
            checkedToPublish: true,
            checkedSchools: checkedSchoolsFromNids(checkedSchoolNids, sortedSchools),
          };
          delete body.pathways;
          await publishMutation.mutateAsync({
            body,
            checkedSchoolNids,
            pathways: cluster.pathways,
          });
          showToast('success', {
            heading: buildPublishedSchoolsSaveSuccessHeading(
              clusterName,
              checkedSchoolNids,
              allSchools
            ),
          });
          onClose();
        } catch {
          showToast('error', {
            heading: buildPublishedSchoolsSaveErrorHeading(clusterName),
          });
        }
      })();
    },
    [allSchools, cluster, clusterName, publishMutation, showToast, onClose, sortedSchools]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <SchoolsDrawer
      key={drawerKey}
      id={drawerId}
      mode="published"
      title={SPECIALIZATION_AREA_PUBLISHED_SCHOOLS_TITLE}
      planName={clusterName}
      description={description}
      showPlanNameHeading={false}
      allSchools={allSchools}
      initialSelectedNids={initialSelectedNids}
      lockedNids={lockedNids}
      isSchoolsLoading={loading}
      onClose={onClose}
      onPrimary={handlePrimary}
      primaryButtonText="Publish"
      disablePrimaryWhenSelectionUnchanged={false}
      primaryButtonDisabled={disablePublish}
    />
  );
}
